import { store } from "@/store";
import Model, { Casts } from "./Model";
import User from "./User";
import type { SubscriptionPlan } from "@/types/Subscription";

interface League {
    id: number;
    name: string;
    logo: string;
}

interface Season {
    id: number;
    name: string;
    is_current: boolean;
    start: string;
    end: string;
}

export interface PouleEntry {
    team: { id: number; name: string; logo?: string | null };
    season: { id: number; name: string };
    league: { name?: string | null; logo?: string | null };
}

export default class Group extends Model<Group>() {
    declare id: number;
    avatar?: string | null;
    avatar_url?: string | null;
    group_image?: string | null;
    avatarUrl?: string | null;
    groupImageUrl?: string | null;
    users?: User[];
    pending_users?: User[];
    admins?: any[];
    canInviteMembers?: boolean;
    user_is_current_owner?: boolean;
    deleted_at?: string; // It can be either null or a string
    name?: string;
    sport?: string;
    league?: League;
    season?: Season;
    poule_id?: number;
    poule_type?: string;
    share_link?: string;
    everyone_can_invite?: boolean;
    everyone_can_rename?: boolean;
    tournament_has_started?: boolean;
    tournament_has_finished?: boolean;
    bonus_questions_close_at?: string | null;
    unanswered_bonus_questions_count?: number;
    plan?: SubscriptionPlan | null;
    user_points?: number;
    user_position?: number;
    players_count?: number;
    user_status?: string;
    has_sub_groups?: boolean;
    sub_groups?: {
        id: number;
        name: string;
        avatar?: string | null;
        avatar_url?: string | null;
        member_count: number;
    }[];
    signature_color?: string | null;
    user_sub_group_id?: number | null;
    is_sub_group?: boolean;
    parent_group_id?: number | null;
    parent_share_link?: string | null;
    parent_signature_color?: string | null;
    current_user_in_sub_group?: boolean;
    current_user_sub_group_status?: "pending" | "accepted" | null;
    is_team_based?: boolean;
    poule_entries?: PouleEntry[];

    public casts: Casts = {
        users: {
            get: (attributes, key) => {
                const users = attributes[key];
                if (!users) {
                    return [];
                }
                // If it's already an array of User instances, return as is
                if (
                    Array.isArray(users) &&
                    users.length > 0 &&
                    users[0] instanceof User
                ) {
                    return users;
                }
                // If it's an array of plain objects, map them to User instances
                if (Array.isArray(users)) {
                    return users.map((user: any) => {
                        if (user instanceof User) {
                            return user;
                        }
                        return new User(user);
                    });
                }
                // If it's a single object, wrap it in an array
                if (typeof users === "object" && users !== null) {
                    return [new User(users)];
                }
                return [];
            },
        },
        pending_users: {
            get: (attributes, key) => {
                const pendingUsers = attributes[key];
                if (!pendingUsers) {
                    return [];
                }
                // If it's already an array of User instances, return as is
                if (
                    Array.isArray(pendingUsers) &&
                    pendingUsers.length > 0 &&
                    pendingUsers[0] instanceof User
                ) {
                    return pendingUsers;
                }
                // If it's an array of plain objects, map them to User instances
                if (Array.isArray(pendingUsers)) {
                    return pendingUsers.map((user: any) => {
                        if (user instanceof User) {
                            return user;
                        }
                        return new User(user);
                    });
                }
                // If it's a single object, wrap it in an array
                if (typeof pendingUsers === "object" && pendingUsers !== null) {
                    return [new User(pendingUsers)];
                }
                return [];
            },
        },
        avatarUrl: {
            get: (attributes) => {
                if (attributes.avatar_url) {
                    return attributes.avatar_url;
                }

                if (!attributes.avatar) {
                    return null;
                }

                if (
                    typeof attributes.avatar === "string" &&
                    (attributes.avatar.startsWith("http://") ||
                        attributes.avatar.startsWith("https://"))
                ) {
                    return attributes.avatar;
                }

                const apiUrl = String(
                    import.meta.env.VUE_APP_API_URL || "",
                ).replace(/\/$/, "");
                const avatarPath =
                    typeof attributes.avatar === "string"
                        ? attributes.avatar
                        : "";

                if (
                    avatarPath.startsWith("/storage/") ||
                    avatarPath.startsWith("storage/")
                ) {
                    return `${apiUrl}/${avatarPath.replace(/^\//, "")}`;
                }

                return `${apiUrl}/storage/${avatarPath}`;
            },
        },
        groupImageUrl: {
            get: (attributes) => {
                if (attributes.group_image) {
                    return attributes.group_image;
                }

                if (attributes.avatar_url) {
                    return attributes.avatar_url;
                }

                if (attributes.avatar) {
                    if (
                        typeof attributes.avatar === "string" &&
                        (attributes.avatar.startsWith("http://") ||
                            attributes.avatar.startsWith("https://"))
                    ) {
                        return attributes.avatar;
                    }

                    const apiUrl = String(
                        import.meta.env.VUE_APP_API_URL || "",
                    ).replace(/\/$/, "");
                    const avatarPath =
                        typeof attributes.avatar === "string"
                            ? attributes.avatar
                            : "";

                    if (
                        avatarPath.startsWith("/storage/") ||
                        avatarPath.startsWith("storage/")
                    ) {
                        return `${apiUrl}/${avatarPath.replace(/^\//, "")}`;
                    }

                    return `${apiUrl}/storage/${avatarPath}`;
                }

                if (attributes.is_team_based) {
                    const entries = attributes.poule_entries ?? [];
                    const seen = new Set<number>();

                    for (const entry of entries) {
                        const teamId = entry?.team?.id;
                        if (!teamId || seen.has(teamId)) {
                            continue;
                        }

                        seen.add(teamId);

                        if (entry.team?.logo) {
                            return entry.team.logo;
                        }
                    }

                    return null;
                }

                return (
                    attributes.league?.logo ||
                    attributes.poule_entries?.[0]?.team?.logo ||
                    null
                );
            },
        },
    };

    isAdmin(userId: number | null = null): boolean {
        if (!userId) {
            userId = store.getters["profile/user"].id;
        }

        return !!this.admins?.find((user) => user?.id === userId);
    }

    canRename(userId: number | null = null): boolean {
        return this.everyone_can_rename || this.isAdmin(userId);
    }

    canInvite(userId: number | null = null): boolean {
        return this.everyone_can_invite || this.isAdmin(userId);
    }
}
